import { uid } from 'uid';
import productService from '@shared/services/productService';

/**
 * DEPRECATED: Legacy form generation
 * Gebruik useRuntimeConfiguratorGeneration voor nieuwe configurators
 *
 * Dit bestand wordt alleen nog gebruikt voor:
 * - Migration van oude configurators
 * - Backwards compatibility
 */
export const useFormGeneration = () => {
  /**
   * Genereert complete configurator form op basis van geselecteerde producten
   */
  const generateConfiguratorForm = async (configuratorData) => {
    const pages = [];

    // Page 1: Product Selection
    pages.push(generateProductSelectionPage(configuratorData));

    // Pages 2+: Pre-generate alle product option pages
    // Extract product IDs from selectedProducts (can be array of strings or objects)
    const selectedProducts = configuratorData.settings.cpq.selectedProducts || [];
    const productIds = selectedProducts
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        return item.productId || item.id;
      })
      .filter(Boolean);

    for (const productId of productIds) {
      try {
        const product = await productService.getProductById(productId);
        if (product && product.options?.length > 0) {
          const productPages = generateProductOptionPages(product);
          pages.push(...productPages);
        }
      } catch (error) {
        console.warn(`Failed to load product ${productId}:`, error);
      }
    }

    // Quote summary page
    pages.push(generateQuoteSummaryPage(configuratorData));

    return {
      ...configuratorData,
      type: 'configurator',
      pages,
      settings: {
        ...configuratorData.settings,
        generatedAt: new Date().toISOString(),
        productCount: configuratorData.settings.cpq.selectedProducts.length,
      },
    };
  };

  /**
   * Genereert product selectie pagina
   */
  const generateProductSelectionPage = (configuratorData) => {
    return {
      id: 'product-selection',
      title: 'Kies uw product',
      description: 'Selecteer het product dat u wilt configureren',
      elements: [
        {
          id: 'product-selection-element',
          type: 'product-selection',
          props: {
            label: 'Product Keuze',
            required: true,
            availableProducts:
              configuratorData.settings.cpq.selectedProducts
                ?.map((item) => {
                  if (typeof item === 'string') return item;
                  return item.productId || item.id;
                })
                .filter(Boolean) || [],
            selectionMode: 'single',
            showPricing: configuratorData.settings.cpq.showPricing,
            showImages: configuratorData.settings.cpq.designSettings.showImages,
          },
        },
      ],
      // Conditional navigation - altijd naar eerste product optie page
      logic: {
        rules: [], // Wordt runtime bepaald op basis van product selectie
        defaultDestination: 'next',
      },
    };
  };

  /**
   * Genereert option pages voor een specifiek product
   */
  const generateProductOptionPages = (product) => {
    const pages = [];

    product.options.forEach((option, index) => {
      const page = {
        id: `${product.id}-${option.id}`,
        title: option.label,
        description: option.caption || `Configureer ${option.label.toLowerCase()}`,
        productId: product.id, // Metadata voor filtering
        optionId: option.id,
        conditionalDisplay: {
          dependsOn: 'product-selection-element',
          showWhen: product.id,
        },
        elements: [
          {
            id: `${product.id}-${option.id}-input`,
            type: mapOptionTypeToElementType(option.type),
            props: {
              label: option.label,
              description: option.caption,
              required: option.required || false,
              ...mapOptionToElementProps(option),
            },
          },
        ],
        logic: {
          rules: [],
          defaultDestination: index < product.options.length - 1 ? 'next' : 'quote-summary',
        },
      };

      pages.push(page);
    });

    return pages;
  };

  /**
   * Genereert quote summary pagina
   */
  const generateQuoteSummaryPage = (configuratorData) => {
    return {
      id: 'quote-summary',
      title: 'Uw Configuratie',
      description: 'Overzicht van uw geselecteerde opties en prijs',
      elements: [
        {
          id: 'quote-summary-element',
          type: 'cpq-quote-summary',
          props: {
            showPricing: configuratorData.settings.cpq.showPricing,
            enableUpsells: configuratorData.settings.cpq.designSettings.enableUpsells,
          },
        },
      ],
      logic: {
        rules: [],
        defaultDestination: 'end',
      },
    };
  };

  /**
   * Map product option type naar form element type
   */
  const mapOptionTypeToElementType = (optionType) => {
    const mapping = {
      number: 'slider',
      'single-choice': 'single-choice',
      'multi-choice': 'multiple-choice',
    };

    return mapping[optionType] || 'text';
  };

  /**
   * Map product option config naar element props
   */
  const mapOptionToElementProps = (option) => {
    const baseProps = { width: option.width || 'full' };

    switch (option.type) {
      case 'number':
        return {
          ...baseProps,
          displayStyle: option.numberConfig?.displayStyle || 'input',
          min: option.numberConfig?.min || 0,
          max: option.numberConfig?.max || 100,
          step: option.numberConfig?.step || 1,
          defaultValue: option.numberConfig?.defaultValue,
          showPrice: true,
          basePrice: option.numberConfig?.basePrice || 0,
          pricePerUnit: option.numberConfig?.pricePerUnit || 0,
        };

      case 'single-choice':
      case 'multi-choice':
        return {
          ...baseProps,
          choices:
            option.choiceConfig?.choices?.map((choice) => ({
              id: choice.id || uid(),
              content: choice.label,
              price: choice.price || 0,
              image: choice.image,
              description: choice.description,
              usps: choice.usps || [],
              isDefault: choice.isDefault || false,
            })) || [],
          display: 'single-column',
          showPrice: true,
          showImage: option.displaySettings?.showImage || false,
          showDescription: option.displaySettings?.showDescription || false,
          showUsps: option.displaySettings?.showUsps || false,
        };

      default:
        return baseProps;
    }
  };

  /**
   * Regenereert pages voor een specifiek product (voor migration)
   */
  const regenerateProductPages = async (form, productId) => {
    try {
      // Remove oude product pages
      const filteredPages = form.pages.filter((page) => page.productId !== productId);

      // Generate nieuwe pages
      const product = await productService.getProductById(productId);
      if (product && product.options?.length > 0) {
        const newPages = generateProductOptionPages(product);

        // Insert nieuwe pages op juiste positie (na product selection)
        const productSelectionIndex = filteredPages.findIndex((p) => p.id === 'product-selection');
        const insertIndex = productSelectionIndex + 1;

        filteredPages.splice(insertIndex, 0, ...newPages);
      }

      return {
        ...form,
        pages: filteredPages,
        settings: {
          ...form.settings,
          lastRegenerated: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error(`Failed to regenerate pages for product ${productId}:`, error);
      return form;
    }
  };

  /**
   * Detecteert wijzigingen tussen huidige product en form pages
   */
  const detectProductChanges = async (form, productId) => {
    try {
      const currentProduct = await productService.getProductById(productId);
      const formPages = form.pages.filter((page) => page.productId === productId);

      const changes = [];

      // Check voor nieuwe opties
      const formOptionIds = formPages.map((page) => page.optionId);
      const currentOptionIds = currentProduct.options?.map((opt) => opt.id) || [];

      const addedOptions = currentOptionIds.filter((id) => !formOptionIds.includes(id));
      const removedOptions = formOptionIds.filter((id) => !currentOptionIds.includes(id));

      addedOptions.forEach((optionId) => {
        const option = currentProduct.options.find((opt) => opt.id === optionId);
        changes.push({
          type: 'option_added',
          optionId,
          optionName: option?.label || 'Unknown',
        });
      });

      removedOptions.forEach((optionId) => {
        const formPage = formPages.find((page) => page.optionId === optionId);
        changes.push({
          type: 'option_removed',
          optionId,
          optionName: formPage?.title || 'Unknown',
        });
      });

      // Check voor gewijzigde opties
      for (const formPage of formPages) {
        if (!formPage.optionId) continue;

        const currentOption = currentProduct.options?.find((opt) => opt.id === formPage.optionId);
        if (!currentOption) continue;

        const formElement = formPage.elements[0];
        const expectedProps = mapOptionToElementProps(currentOption);

        // Vergelijk props (simplified)
        if (JSON.stringify(expectedProps) !== JSON.stringify(formElement.props)) {
          changes.push({
            type: 'option_modified',
            optionId: currentOption.id,
            optionName: currentOption.label,
            details: {
              current: expectedProps,
              form: formElement.props,
            },
          });
        }
      }

      return changes;
    } catch (error) {
      console.error(`Failed to detect changes for product ${productId}:`, error);
      return [];
    }
  };

  return {
    generateConfiguratorForm,
    generateProductSelectionPage,
    generateProductOptionPages,
    generateQuoteSummaryPage,
    regenerateProductPages,
    detectProductChanges,
    mapOptionTypeToElementType,
    mapOptionToElementProps,
  };
};
